import { assign, createMachine } from "xstate";
import { localStorageKey, stringifyData } from "./AppMachineStorage";
import {
  AppContext,
  AppEvent,
  AppState,
  EventTypes,
  initialContext,
  ReadCachedExpiryAndDataEvent,
  ReceiveErrorEvent,
  ReceiveExpiryAndDataFromNetworkEvent,
} from "./AppMachineUtils";

const captureException = (context: AppContext, event: ReceiveErrorEvent) => {
  console.error("Application error:", event.error);
};

const setViewerData = assign(
  (
    context: AppContext,
    event: ReadCachedExpiryAndDataEvent | ReceiveExpiryAndDataFromNetworkEvent,
  ) => ({
    expiry: event.expiry,
    viewer: event.viewer,
    employee: event.employee,
    permissions: event.permissions,
    flags: event.flags,
    preferences: event.preferences,
  }),
);

const resetViewerData = assign<AppContext, AppEvent>({
  expiry: undefined,
  viewer: undefined,
  employee: undefined,
  permissions: undefined,
  flags: undefined,
});

export function resetLocalStorage() {
  localStorage.removeItem(localStorageKey);
}

export function saveToLocalStorage(
  context: AppContext,
  event: ReceiveExpiryAndDataFromNetworkEvent,
) {
  localStorage.setItem(
    localStorageKey,
    stringifyData({
      expiry: event.expiry,
      viewer: event.viewer,
      employee: event.employee,
      permissions: event.permissions,
      flags: event.flags,
      preferences: event.preferences,
    }),
  );
}

export const AppMachine = createMachine<AppContext, AppEvent, AppState>({
  id: "AppController",
  context: initialContext,
  initial: "pending",
  states: {
    pending: {
      // @ts-ignore
      on: {
        [EventTypes.readCachedExpiryAndData]: {
          target: "probablyAuthenticated",
          actions: [setViewerData],
        },
        [EventTypes.readEmptyExpiryAndData]: {
          target: "probablyUnauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveExpiryAndDataFromNetwork]: {
          target: "authenticated",
          actions: [saveToLocalStorage, setViewerData],
        },
        [EventTypes.receiveAuthenticationErrorFromNetwork]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNonEmployee]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNetworkError]: {
          target: "error",
          actions: [captureException, resetViewerData],
        },
        [EventTypes.readExpired]: {
          target: "probablyUnauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
      },
    },

    probablyAuthenticated: {
      // @ts-ignore
      on: {
        [EventTypes.receiveExpiryAndDataFromNetwork]: {
          target: "authenticated",
          actions: [saveToLocalStorage, setViewerData],
        },
        [EventTypes.receiveAuthenticationErrorFromNetwork]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNonEmployee]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNetworkError]: {
          target: "error",
          actions: [captureException, resetViewerData],
        },
      },
    },

    probablyUnauthenticated: {
      // @ts-ignore
      on: {
        [EventTypes.receiveExpiryAndDataFromNetwork]: {
          target: "authenticated",
          actions: [saveToLocalStorage, setViewerData],
        },
        [EventTypes.receiveAuthenticationErrorFromNetwork]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNonEmployee]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNetworkError]: {
          target: "error",
          actions: [captureException, resetViewerData],
        },
      },
    },

    authenticated: {
      // @ts-ignore
      // TODO: make saveToLocalStorage an entry action
      on: {
        // Update cached data
        [EventTypes.receiveExpiryAndDataFromNetwork]: {
          target: "authenticated",
          actions: [saveToLocalStorage, setViewerData],
        },
        [EventTypes.receiveAuthenticationErrorFromNetwork]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNonEmployee]: {
          target: "unauthenticated",
          actions: [resetLocalStorage, resetViewerData],
        },
        [EventTypes.receiveNetworkError]: {
          target: "error",
          actions: [captureException, resetViewerData],
        },
      },
    },

    unauthenticated: {
      // @ts-ignore
      on: {
        [EventTypes.receiveNetworkError]: {
          target: "error",
          actions: [captureException, resetViewerData],
        },
      },
    },

    error: {
      type: "final",
    },
  },
});
